import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useAuctionHouse } from '../../contexts/AuctionHouseContext';
import { UserRole } from '../../types/user.types';

const Header: React.FC = () => {
  const { currentUser, userProfile, logout } = useAuth();
  const { currentAuctionHouse } = useAuctionHouse();
  const navigate = useNavigate();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // Determine which dashboard link to show
  const getDashboardLink = () => {
    if (!userProfile) return '/dashboard';
    
    if (userProfile.role === UserRole.ADMIN || userProfile.role === UserRole.AUCTIONEER) {
      return '/auctioneer/dashboard';
    }
    
    return '/dashboard';
  };

  const getDashboardLabel = () => {
    if (!userProfile) return 'Dashboard';
    
    if (userProfile.role === UserRole.ADMIN || userProfile.role === UserRole.AUCTIONEER) {
      return 'Auctioneer Dashboard';
    }
    
    return 'Dashboard';
  };

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo/Brand */}
          <Link to="/" className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-primary-600">
              {currentAuctionHouse ? currentAuctionHouse.name : 'AuctionRinger'}
            </span>
          </Link>

          {/* Navigation */}
          <nav className="hidden md:flex items-center space-x-6">
            <Link 
              to="/" 
              className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
            >
              Home
            </Link>
            
            <Link 
              to="/auctions" 
              className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
            >
              Auctions
            </Link>

            {currentUser && (
              <>
                <Link 
                  to="/predictions" 
                  className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                >
                  My Predictions
                </Link>
                
                <Link 
                  to="/leaderboard" 
                  className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
                >
                  Leaderboard
                </Link>
              </>
            )}

            <Link 
              to="/how-it-works" 
              className="text-gray-700 hover:text-primary-600 font-medium transition-colors"
            >
              How It Works
            </Link>

            {/* Admin Dashboard Link */}
            {userProfile?.role === UserRole.ADMIN && (
              <Link 
                to="/admin/dashboard" 
                className="text-red-600 hover:text-red-700 font-semibold transition-colors"
              >
                Admin Dashboard
              </Link>
            )}
          </nav>

          {/* User Menu */}
          <div className="flex items-center space-x-4">
            {currentUser ? (
              <>
                {/* Auctioneer/Admin Dashboard Button */}
                {(userProfile?.role === UserRole.AUCTIONEER || userProfile?.role === UserRole.ADMIN) && (
                  <Link
                    to="/auctioneer/dashboard"
                    className="hidden md:block bg-purple-600 text-white px-4 py-2 rounded-lg font-semibold hover:bg-purple-700 transition-colors"
                  >
                    {getDashboardLabel()}
                  </Link>
                )}

                {/* Regular User Dashboard */}
                {userProfile?.role === UserRole.RINGER && (
                  <Link
                    to="/dashboard"
                    className="hidden md:block text-gray-700 hover:text-primary-600 font-medium transition-colors"
                  >
                    Dashboard
                  </Link>
                )}

                {/* User Menu Dropdown */}
                <div className="relative group">
                  <button className="flex items-center space-x-2 text-gray-700 hover:text-primary-600">
                    <div className="w-8 h-8 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                      {userProfile?.displayName?.[0]?.toUpperCase() || 'U'}
                    </div>
                    <span className="hidden md:inline font-medium">
                      {userProfile?.displayName || 'User'}
                    </span>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 invisible group-hover:visible opacity-0 group-hover:opacity-100 transition-all">
                    <Link
                      to={getDashboardLink()}
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      {getDashboardLabel()}
                    </Link>
                    
                    <Link
                      to="/predictions"
                      className="block px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      My Predictions
                    </Link>

                    {userProfile?.role === UserRole.ADMIN && (
                      <Link
                        to="/admin/dashboard"
                        className="block px-4 py-2 text-red-600 hover:bg-gray-100 font-semibold"
                      >
                        Admin Dashboard
                      </Link>
                    )}

                    <hr className="my-2" />
                    
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      Logout
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-primary-600 font-medium"
                >
                  Login
                </Link>
                <Link
                  to="/register"
                  className="bg-primary-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-primary-700 transition-colors"
                >
                  Register
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button className="md:hidden text-gray-700">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;